import React, { useMemo } from "react";
import { Calendar, Info } from "lucide-react";
import { cn } from "../../../../lib/cn";

/**
 * PreviewSlotsWizard — visualiza los slots que se generarán para
 * la campaña, según las franjas + fechas + duración configuradas
 * en el paso 3 del wizard.
 *
 * Solo lectura: durante la creación, la campaña aún no existe en
 * BBDD y los técnicos no están asignados. Los bloqueos por técnico
 * se hacen tras crear la campaña (Fase 12d).
 *
 * Props:
 *   payload: { fecha_inicio, fecha_limite, duracion_cita, franjas }
 *
 * Agrupa por semana ISO para facilitar la lectura de rangos largos.
 * Si el rango excede ~8 semanas, muestra las 8 primeras + resumen
 * del resto (evita listas kilométricas).
 */

const DIAS_ABBR = ["", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MESES_ABBR = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];
const MAX_SEMANAS_MOSTRADAS = 8;

function pad(n) { return String(n).padStart(2, "0"); }
function timeAMin(t) {
  if (!t) return 0;
  const [h, m] = String(t).split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}
function fechaADiaISO(date) {
  const js = date.getDay();
  return js === 0 ? 7 : js;
}
function formatearFecha(date) {
  return `${DIAS_ABBR[fechaADiaISO(date)]} ${date.getDate()} ${MESES_ABBR[date.getMonth()]}`;
}

/**
 * Devuelve el lunes de la semana ISO que contiene la fecha dada.
 * (Semana empieza en lunes según ISO 8601.)
 */
function lunesDeLaSemana(date) {
  const d = new Date(date);
  const dia = d.getDay();
  const offsetLun = dia === 0 ? -6 : 1 - dia;
  d.setDate(d.getDate() + offsetLun);
  d.setHours(12, 0, 0, 0);
  return d;
}

function keySemana(date) {
  const lun = lunesDeLaSemana(date);
  return `${lun.getFullYear()}-${pad(lun.getMonth() + 1)}-${pad(lun.getDate())}`;
}

// ────────────────────────────────────────────────────────────────

export default function PreviewSlotsWizard({ payload }) {

  const {
    fecha_inicio, fecha_limite, duracion_cita, franjas,
  } = payload || {};

  const analisis = useMemo(() => {
    if (!fecha_inicio || !fecha_limite || !duracion_cita) return null;
    if (!Array.isArray(franjas) || franjas.length === 0) return null;

    const inicio = new Date(`${fecha_inicio}T12:00:00`);
    const fin = new Date(`${fecha_limite}T12:00:00`);
    if (inicio > fin) return null;

    // Franjas indexadas por día_semana
    const franjasPorDia = {};
    for (const f of franjas) {
      if (!franjasPorDia[f.dia_semana]) franjasPorDia[f.dia_semana] = [];
      franjasPorDia[f.dia_semana].push(f);
    }
    for (const dia of Object.keys(franjasPorDia)) {
      franjasPorDia[dia].sort(
        (a, b) => timeAMin(a.hora_inicio) - timeAMin(b.hora_inicio)
      );
    }

    // Iterar días del rango
    const semanas = new Map();
    let totalSlots = 0;
    let totalDiasTrabajo = 0;
    let totalDiasSinTrabajo = 0;

    const cur = new Date(inicio);
    while (cur <= fin) {
      const diaISO = fechaADiaISO(cur);
      const franjasDia = franjasPorDia[diaISO] || [];

      let slotsDia = 0;
      const franjasInfo = [];
      for (const f of franjasDia) {
        const ini = timeAMin(f.hora_inicio);
        const finF = timeAMin(f.hora_fin);
        const n = Math.floor((finF - ini) / duracion_cita);
        slotsDia += n;
        franjasInfo.push({
          hora_inicio: String(f.hora_inicio).slice(0, 5),
          hora_fin: String(f.hora_fin).slice(0, 5),
          slots: n,
        });
      }

      const infoDia = {
        fecha: new Date(cur),
        diaISO,
        slotsDia,
        franjasInfo,
        trabaja: slotsDia > 0,
      };

      totalSlots += slotsDia;
      if (infoDia.trabaja) totalDiasTrabajo++;
      else totalDiasSinTrabajo++;

      const key = keySemana(cur);
      if (!semanas.has(key)) {
        semanas.set(key, {
          lunes: lunesDeLaSemana(cur),
          dias: [],
        });
      }
      semanas.get(key).dias.push(infoDia);

      cur.setDate(cur.getDate() + 1);
    }

    return {
      semanas: Array.from(semanas.values()),
      totalSlots,
      totalDiasTrabajo,
      totalDiasSinTrabajo,
    };
  }, [fecha_inicio, fecha_limite, duracion_cita, franjas]);

  // ────────────────────────────────────────────────────────────
  //  Render
  // ────────────────────────────────────────────────────────────

  if (!analisis) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-3">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          Preview del calendario
        </div>
        <div className="text-xs text-slate-400 text-center py-8">
          Configura fechas, duración y franjas para ver el preview.
        </div>
      </div>
    );
  }

  const { semanas, totalSlots, totalDiasTrabajo, totalDiasSinTrabajo } = analisis;
  const semanasMostradas = semanas.slice(0, MAX_SEMANAS_MOSTRADAS);
  const semanasOcultas = semanas.length - semanasMostradas.length;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white flex flex-col">
      {/* Cabecera con totales */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-2">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          Preview del calendario
        </div>
        <div className="text-2xl font-semibold text-slate-900 tabular-nums leading-tight">
          {totalSlots.toLocaleString("es-ES")}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          slots totales · {totalDiasTrabajo} {totalDiasTrabajo === 1 ? "día" : "días"} laborables
          {totalDiasSinTrabajo > 0 && (
            <> · {totalDiasSinTrabajo} sin trabajo</>
          )}
        </div>
      </div>

      {/* Lista de semanas */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[500px]">
        {semanasMostradas.map((sem, i) => (
          <SemanaCard key={i} semana={sem} />
        ))}

        {semanasOcultas > 0 && (
          <div className="text-[11px] text-slate-500 text-center py-2 flex items-center justify-center gap-1.5">
            <Info className="w-3 h-3" />
            {semanasOcultas} {semanasOcultas === 1 ? "semana más" : "semanas más"} no mostradas
          </div>
        )}
      </div>

      {/* Aviso pie */}
      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50">
        <p className="text-[10px] text-slate-400 leading-relaxed">
          Los bloqueos por técnico se gestionan tras crear la campaña, desde el
          calendario.
        </p>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────

function SemanaCard({ semana }) {
  const { lunes, dias } = semana;
  const mesLunes = MESES_ABBR[lunes.getMonth()];

  return (
    <div className="border border-slate-100 rounded-md overflow-hidden">
      <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
        Semana del {lunes.getDate()} de {mesLunes}
      </div>
      <div className="divide-y divide-slate-50">
        {dias.map((dia, i) => (
          <DiaRow key={i} dia={dia} />
        ))}
      </div>
    </div>
  );
}

function DiaRow({ dia }) {
  return (
    <div className={cn(
      "px-3 py-1.5 flex items-baseline justify-between gap-2",
      !dia.trabaja && "bg-slate-50/50"
    )}>
      <div className="min-w-0 flex-1">
        <div className={cn(
          "text-xs tabular-nums",
          dia.trabaja ? "text-slate-900 font-medium" : "text-slate-400"
        )}>
          {formatearFecha(dia.fecha)}
        </div>
        {dia.trabaja && (
          <div className="text-[10px] text-slate-500 mt-0.5">
            {dia.franjasInfo.map((f, i) => (
              <span key={i} className="mr-2 tabular-nums">
                {f.hora_inicio}-{f.hora_fin}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        {dia.trabaja ? (
          <div className="text-xs font-semibold text-slate-900 tabular-nums">
            {dia.slotsDia}
            <span className="text-[10px] text-slate-400 font-normal ml-0.5">slots</span>
          </div>
        ) : (
          <span className="text-[10px] text-slate-400 italic">sin trabajo</span>
        )}
      </div>
    </div>
  );
}
